import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Check, ArrowRight, Building2, Users, FileText, Calculator, Calendar, Shield, Smartphone, Cloud, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';

function LogicielSyndicGratuit() {
  const features = [
    { icon: Users, title: "Gestion des copropriétaires", desc: "Liste complète avec tantièmes, lots et coordonnées" },
    { icon: FileText, title: "Assemblées générales", desc: "Convocations, feuilles de présence et PV automatiques" },
    { icon: Calculator, title: "Appels de fonds", desc: "Calcul automatique et génération PDF" },
    { icon: Calendar, title: "Calendrier partagé", desc: "Événements et rappels pour tous" },
    { icon: Shield, title: "Documents sécurisés", desc: "Stockage et partage des documents officiels" },
    { icon: Zap, title: "Assistant IA", desc: "Aide juridique et réponses instantanées" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Logiciel Syndic Bénévole Gratuit 2026 | Application Gestion Copropriété</title>
        <meta name="description" content="Logiciel de gestion de copropriété gratuit pour syndic bénévole. Application en ligne simple : assemblées générales, appels de fonds, comptabilité. C'est gratuit 14 jours." />
        <meta name="keywords" content="logiciel syndic bénévole gratuit, application copropriété, gestion copropriété en ligne, app copro, logiciel gestion copropriété, outil syndic bénévole" />
        <link rel="canonical" href="https://copropriete.app/logiciel-syndic-gratuit" />
      </Helmet>

      {/* Header */}
      <header className="bg-slate-900 text-white py-4">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            copropriete.app
          </Link>
          <Link to="/login" className="bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg text-sm font-medium">
            C'est gratuit
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <span className="inline-block bg-emerald-500/20 text-emerald-400 px-4 py-1 rounded-full text-sm font-medium mb-6">
            C'est gratuit 14 jours - Sans engagement
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Logiciel de gestion de copropriété<br />
            <span className="text-indigo-400">gratuit pour syndic bénévole</span>
          </h1>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            L'application en ligne la plus simple pour gérer votre copropriété : assemblées générales, appels de fonds, comptabilité, documents... Tout en un seul outil.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-lg px-8">
                Démarrer gratuitement <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
          <p className="text-sm text-slate-400 mt-4">
            Aucune carte bancaire requise • Accès immédiat
          </p>
        </div>
      </section>

      {/* Keywords Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-4">
            L'application copropriété pensée pour les syndics bénévoles
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            copropriete.app est un <strong>logiciel de gestion de copropriété en ligne</strong> qui simplifie toutes les tâches administratives du syndic bénévole.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Online */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            Pourquoi choisir une app de copropriété en ligne ?
          </h2>
          
          <div className="space-y-6">
            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Cloud className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Accessible partout, tout le temps</h3>
                <p className="text-slate-600">Connectez-vous depuis votre ordinateur, tablette ou smartphone. Vos données sont synchronisées en temps réel et accessibles 24h/24.</p>
              </div>
            </div>
            
            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Interface mobile optimisée</h3>
                <p className="text-slate-600">Notre <strong>application de gestion de copropriété</strong> est conçue pour fonctionner parfaitement sur mobile. Gérez votre copro même en déplacement.</p>
              </div>
            </div>
            
            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Sécurité et sauvegardes automatiques</h3>
                <p className="text-slate-600">Vos données sont chiffrées et sauvegardées automatiquement. Fini les fichiers Excel perdus ou les documents égarés.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            Logiciel syndic gratuit vs payant : que choisir ?
          </h2>
          
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left py-4 px-6 font-semibold text-slate-900">Fonctionnalité</th>
                  <th className="text-center py-4 px-6 font-semibold text-slate-900">copropriete.app</th>
                  <th className="text-center py-4 px-6 font-semibold text-slate-500">Excel/Gratuit</th>
                </tr>
              </thead>
              <tbody>
                {[
                  "Gestion des copropriétaires",
                  "Calcul automatique des charges",
                  "Génération des convocations AG",
                  "Procès-verbaux automatiques",
                  "Appels de fonds PDF",
                  "Assistant juridique IA",
                  "Stockage documents",
                  "Accès mobile",
                ].map((feature, index) => (
                  <tr key={index} className="border-t border-slate-100">
                    <td className="py-3 px-6 text-slate-700">{feature}</td>
                    <td className="py-3 px-6 text-center"><Check className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                    <td className="py-3 px-6 text-center text-slate-400">—</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <p className="text-center text-slate-600 mt-6">
            Notre <strong>outil de gestion de copropriété</strong> vous fait gagner des heures chaque mois.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-indigo-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Testez gratuitement notre logiciel de copropriété
          </h2>
          <p className="text-indigo-100 mb-8 text-lg">
            100% gratuit, sans engagement, sans carte bancaire
          </p>
          <Link to="/login">
            <Button size="lg" className="bg-white text-indigo-600 hover:bg-indigo-50 text-lg px-8">
              Créer mon compte gratuit <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* FAQ Schema */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Questions fréquentes</h2>
          
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Le logiciel est-il vraiment gratuit ?</h3>
              <p className="text-slate-600">copropriete.app propose un gratuit de 14 jours avec accès à toutes les fonctionnalités. Ensuite, l'abonnement est de 49€/mois ou 490€/an (2 mois offerts).</p>
            </div>
            
            <div className="bg-slate-50 rounded-lg p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Faut-il installer quelque chose ?</h3>
              <p className="text-slate-600">Non, notre application de gestion de copropriété fonctionne 100% en ligne. Accédez-y depuis n'importe quel navigateur web.</p>
            </div>
            
            <div className="bg-slate-50 rounded-lg p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Mes données sont-elles sécurisées ?</h3>
              <p className="text-slate-600">Oui, toutes vos données sont chiffrées et stockées sur des serveurs sécurisés en Europe. Nous effectuons des sauvegardes quotidiennes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h4 className="font-semibold text-white mb-4">copropriete.app</h4>
              <p className="text-sm">Le logiciel de gestion de copropriété en ligne pour syndics bénévoles.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Produit</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="hover:text-white">Accueil</Link></li>
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Ressources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/syndic-benevole" className="hover:text-white">Syndic bénévole</Link></li>
                <li><Link to="/assemblee-generale-copropriete" className="hover:text-white">Assemblée générale</Link></li>
                <li><Link to="/charges-copropriete" className="hover:text-white">Charges copropriété</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <p className="text-sm">gmpdeveloppement@gmail.com</p>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm">
            <p>© 2026 copropriete.app - Application de gestion de copropriété en ligne</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LogicielSyndicGratuit;
